import {loadConfig, getConfig, AppConfig} from '../config/appConfig';

class ConfigService {
  private static instance: ConfigService;
  private config: AppConfig;
  private initialized = false;

  private constructor() {
    // Initialiser avec des valeurs par défaut pour permettre l'utilisation immédiate
    // Détecter l'environnement : __DEV__ pour React Native, process.env.NODE_ENV pour web
    const isDev = 
      (typeof __DEV__ !== 'undefined' && __DEV__) ||
      (typeof process !== 'undefined' && process.env && process.env.NODE_ENV === 'development');
    const environment = isDev ? 'development' : 'production';
    
    // Valeurs par défaut - toujours définies
    this.config = {
      environment,
      supabase: {
        url: 'https://szqaesvnpregunikubqz.supabase.co',
        anonKey: 'sb_publishable_jVe1MLFuwO38SCDqn3xKmA_sifp0sqg',
      },
      api: {
        baseUrl: 'http://localhost:8088',
        timeout: 30000,
      },
      debug: {
        enabled: isDev,
        logLevel: isDev ? 'debug' : 'error',
      },
    };
    
    // Surcharger avec les variables d'environnement si disponibles
    // Web (Vite) : import.meta.env.VITE_* — la config réelle sera chargée dans initialize() via loadConfig()
    if (typeof import.meta !== 'undefined' && (import.meta as any).env) {
      const env = (import.meta as any).env as Record<string, string | undefined>;
      if (env.VITE_SUPABASE_URL) this.config.supabase.url = env.VITE_SUPABASE_URL;
      if (env.VITE_SUPABASE_ANON_KEY) this.config.supabase.anonKey = env.VITE_SUPABASE_ANON_KEY;
      else if (env.VITE_SUPABASE_PUBLISHABLE_KEY) this.config.supabase.anonKey = env.VITE_SUPABASE_PUBLISHABLE_KEY;
      if (env.VITE_API_BASE_URL) this.config.api.baseUrl = env.VITE_API_BASE_URL;
      if (env.VITE_ENVIRONMENT) this.config.environment = env.VITE_ENVIRONMENT;
    } else if (typeof process !== 'undefined' && process.env) {
      if (process.env.SUPABASE_URL) this.config.supabase.url = process.env.SUPABASE_URL;
      if (process.env.SUPABASE_PUBLISHABLE_KEY) {
        this.config.supabase.anonKey = process.env.SUPABASE_PUBLISHABLE_KEY;
      } else if (process.env.SUPABASE_ANON_KEY) {
        this.config.supabase.anonKey = process.env.SUPABASE_ANON_KEY;
      }
      if (process.env.API_BASE_URL) this.config.api.baseUrl = process.env.API_BASE_URL;
    }

    // Log pour confirmer l'initialisation
    console.log('[ConfigService] Constructor called, config initialized:', this.config);
  }

  static getInstance(): ConfigService {
    if (!ConfigService.instance) {
      ConfigService.instance = new ConfigService();
    }
    return ConfigService.instance;
  }

  async initialize(): Promise<void> {
    if (this.initialized) {
      return;
    }

    // Charger la config (peut surcharger les valeurs par défaut)
    this.config = await loadConfig();
    this.initialized = true;
  }

  get environment(): string {
    return this.getConfig().environment;
  }

  get supabaseAnonKey(): string {
    return this.getConfig().supabase.anonKey;
  }

  get supabaseUrl(): string {
    return this.getConfig().supabase.url;
  }

  get apiBaseUrl(): string {
    return this.getConfig().api.baseUrl;
  }

  get apiTimeout(): number {
    return this.getConfig().api.timeout;
  }

  get debugEnabled(): boolean {
    return this.getConfig().debug.enabled;
  }

  get logLevel(): string {
    return this.getConfig().debug.logLevel;
  }

  get isDevelopment(): boolean {
    return this.environment === 'development';
  }

  get isProduction(): boolean {
    return this.environment === 'production';
  }

  private getConfig(): AppConfig {
    return this.config;
  }
}

export default ConfigService.getInstance();
